import type { Schemas } from '@npc/api-client'
import { useEffect, useRef, useState } from 'react'

import { API_BASE } from '../../app/api'
import { render } from '../../copy'

/**
 * 订阅一次接入的探测流。
 *
 * 走 `EventSource`：契约里 `/onboarding/{id}/events` 是 `text/event-stream`，
 * 断线由浏览器自己带 `Last-Event-ID` 重连，我们不自己写重连逻辑——
 * 事件里的 `cursor` 就是那个 id，服务端据它补齐。
 *
 * 每条事件按 `step` 去重后覆盖：同一步会先来 `running` 再来 `ok`，
 * 界面上那一行原地从「正在…」变成「✓」，而不是多出一行。
 *
 * ## 两处上线才会现形的东西
 *
 * **一、`withCredentials`。** `EventSource` 默认**不带 cookie 跨源**，
 * 而登录态就是一个 httpOnly Cookie。同源部署时无所谓，
 * 一旦 `VITE_API_BASE` 指到另一个域（api.arronx.ai 这种），
 * 这条流会拿不到会话——**而它失败的样子是「探测一直不出结果」**，
 * 不是「你没登录」。API 客户端那边早就是 `credentials: 'include'` 了，
 * 这里是同一个决定，只是当初漏在了 SSE 这一侧。
 *
 * **二、断了要说话。** 原来 `onerror` 一行都没有：流一断，界面就停在
 * 「正在探测」上等下去。而「贴一次 SSH，40 秒出档案」是这一屏的验收标准，
 * **一个静默的卡住是这里最坏的结果**——用户不知道该等还是该重来。
 *
 * 浏览器会自己重连，所以只有 `readyState === CLOSED`（它放弃了，
 * 401 / CORS / 证书这类）才算真失败。还在重连的不报——那只是慢。
 */
/**
 * 空转几轮之后就不再重连。
 *
 * **它不是「结束了」的判据**——那条判据只有一个来源，是后端给的状态
 * （`noMoreEvents`）。这个是断路器：**当那个来源本身取不到的时候**
 * （状态接口 404、会话过期、反代把它挡了），上面那条判据永远为假，
 * 而浏览器会一直重连下去。线上量到的是 140 秒 43 次。
 *
 * CLAUDE.md §8：**一件活能被重新取走，前提是有什么变了。**
 * 一轮下来什么都没变的重连不是重试，是同一件事被无限重放。
 * 收到任何一条事件就归零——**那说明这条流是活的，重连是有意义的**。
 */
const MAX_IDLE_RETRIES = 5

export function useProbeStream(
  onboardingId: string | null,
  /**
   * 这次接入还会不会有新事件（见 `app/onboarding-status.ts` 的 `noMoreEvents`）。
   *
   * **它管的是「还要不要重连」，不管「开不开这一次」。** 理由见下面那段。
   */
  finished: boolean,
): {
  events: Schemas['OnboardingEvent'][]
  failure: string | null
} {
  const [events, setEvents] = useState<Schemas['OnboardingEvent'][]>([])
  const [failure, setFailure] = useState<string | null>(null)

  /**
   * 「还会不会有新事件」的**最新值**，给重连那一刻读。
   *
   * 不把它放进 effect 的依赖里：放进去的话，状态每变一次就会把
   * 正在收事件的那条流拆掉重开一条——**而那正是这一条 hook 要避免的事**。
   */
  const finishedRef = useRef(finished)
  finishedRef.current = finished

  useEffect(() => {
    if (!onboardingId) {
      setEvents([])
      setFailure(null)
      return
    }

    /**
     * **到终点了就不要再*重*连。注意是重连，不是这一次。**
     *
     * 线上撞到的：`status = failed`、`finished_at` 也有了，**而前端还在
     * 大约每 3 秒重连一次 SSE**（实测 140 秒 43 次）——它不认这个终点。
     * `EventSource` 的自动重连是**无条件**的：服务端把流关掉（正常收尾也是关流）
     * 在它看来与「网络断了」一模一样。**「这次结束了」这件事只有我们知道。**
     *
     * ## 上一版把这条判据用在了错的地方
     *
     * 上一版写的是「`if (finished) return`」——**连开都不开**。
     * 而 `status` 完全可能在我们连上之前就已经是终态了
     * （`awaiting_confirm` 尤其快：探测跑完、猜出结果、等他点头，
     * 而那一屏正要把刚才那几步逐条画出来）。
     *
     * 后果是**整条探测流一条都不渲染**：右边停在「正在认这台机器…」，
     * 「连上了」「换上了小队自己的钥匙」再也不出现。
     * e2e 那条用例抓到的就是它，而单元用例没抓到——
     * 单元用例数的是「发了几次请求」，**而「一次都不发」在那个口径下是满分**。
     *
     * 这是同一个洞的另一侧：上一版漏了走得最少的那一档，
     * 这一版修的时候撞坏了走得最多的那一条。
     *
     * ## 所以判据挪到了重连那一刻
     *
     * 流照开——**历史事件要靠这一次连接补齐**，不开就永远没有。
     * 服务端关流之后浏览器要重来的那一刻才问一句「还会有新东西吗」，
     * 没有就当场 `close()`。既不会白重连，也不会一条都收不到。
     */
    const url = `${API_BASE}/onboarding/${onboardingId}/events`
    const source = new EventSource(url, { withCredentials: true })
    // 从上一条事件到现在，浏览器空重连了几次
    let idleRetries = 0

    source.onmessage = (message: MessageEvent<string>) => {
      idleRetries = 0
      const event = JSON.parse(message.data) as Schemas['OnboardingEvent']
      setEvents((previous) => {
        const index = previous.findIndex((item) => item.step === event.step)
        if (index === -1) return [...previous, event]
        const next = [...previous]
        next[index] = event
        return next
      })
    }

    source.onerror = () => {
      // 还在重连的不算失败——浏览器自己会重来，那只是慢。
      // **但不能让它一直重下去**：断路器在这儿，理由见 MAX_IDLE_RETRIES
      if (source.readyState !== EventSource.CLOSED) {
        /**
         * 浏览器打算重连了。**问一句「还会有新东西吗」——这就是那个判据
         * 该出场的地方**，而不是在开流之前。
         *
         * 取 ref 不取闭包里那个 `finished`：这个 effect 只跟着
         * `onboardingId` 跑，不跟着状态跑（跟着状态跑的话，
         * 状态一变就会把正在收事件的那条流拆掉重开）。
         */
        if (finishedRef.current) {
          source.close()
          return
        }

        idleRetries += 1
        if (idleRetries < MAX_IDLE_RETRIES) return

        console.error(
          `[npc] 探测流空重连了 ${idleRetries} 次，一条事件都没收到，我不再重连了：${url}\n` +
            `  **先别去查这条流本身。** 正常收尾也会关流，而关流之后该停下来的判据\n` +
            `  来自 GET /onboarding/{id} 的 status——所以最可能的是那一条取不到\n` +
            `  （404 / 会话过期 / 反代挡了），而不是这条 SSE 有毛病。`,
        )
        source.close()
        setFailure(render('error.generic'))
        return
      }

      console.error(
        `[npc] 探测流断了，而且浏览器放弃重连了：${url}\n` +
          `  这条是 SSE，登录态走 httpOnly Cookie。跨源部署时它必须 withCredentials，\n` +
          `  而且服务端要回 Access-Control-Allow-Credentials 与具体的 Origin（不能是 *）。\n` +
          `  另两个常见原因：反代把 text/event-stream 缓冲了（要关 proxy_buffering）、\n` +
          `  或者这条会话已经过期。`,
      )
      setFailure(render('error.generic'))
    }

    return () => source.close()
  }, [onboardingId])

  return { events, failure }
}
